/**
 * 轮转：把这一天的照片排成一圈倾斜的光环，转着看（docs/prototype-v0.1.md §4.3，做法和参数同原型）。
 *
 * 原型把所有照片排成一圈；正式版只转当前星团（同一天）的照片（design §3.3、§2 第 7 条），从照片面板的「轮转」进来：
 * - 照片从这一天的星里飞起，正面的先到，排成一圈倾斜的光环，悬在星团上方；正面那张放大 1.25 倍。
 * - 拖动、滚轮、← → 转动，松手 0.25 秒后吸附到最近一格；点侧面的转到正面，点正面的回到那张的照片面板。
 * - 光环固定 12 格（原型的示例正好 12 张；格子再多就挤成一堵墙，太少则只有正面那张朝着镜头）。
 *   照片多于 12 张时，转过背面的那一格换成后面的照片；少于 12 张时绕着重复放。所以只有正面附近的照片
 *   做成卡片、加载 512 缩略图；槽位固定、用完回收，和 ADR-0004 的近档面片池一致。只有两张时用前后两格。
 * 卡片画在单独的场景里，由 post.ts 在色调映射之后叠上去：不参与辉光，但照样被流体扭曲（同原型 §4.4）。
 * 尺寸都按原型的单位写（卡片面积 100），整个光环再缩小 K 倍放进星海。
 */
import * as THREE from 'three';

export type CardImage = HTMLImageElement | HTMLCanvasElement;

export interface CarouselHooks {
  /** 先显示的模糊图（ThumbHash）或主色渐变；没有就返回 null */
  placeholder(star: number): Promise<CardImage | null>;
  /** 清晰的 512 缩略图；没有缩略图时返回 null */
  load(star: number, signal: AbortSignal): Promise<CardImage | null>;
  /** 正面换成了这一天的第 k 张（共 n 张） */
  onFront(star: number, k: number, n: number): void;
  /** 点了正面那张：回到它的照片面板 */
  onOpen(): void;
}

/** 原型的单位换到星海里：原型的卡片约 11.5 × 8.7，这里约 3.5 × 2.6（星团里两颗星相距 0.17） */
const K = 0.3;
const UP = new THREE.Vector3(0, 1, 0);
const TILT = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), 0.1);
const FRONT_SCALE = 1.25;
/** 光环悬在星团上方多高（原型的光环中心在星系上方 44） */
const LIFT = 44;
const MARGIN = 1.6;
/** 起飞错峰：正面的先到，背面的后到 */
const STAGGER = 0.55;
/** 镜头的注视点比原型低一点，光环在画面里偏上，给底下的字幕留出地方 */
const AIM_DOWN = 4;

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// 原型的卡片着色器，去掉了溶解（这里的照片不在星海里溶解）
const FRAG = /* glsl */ `
uniform sampler2D uMap;
// 照片按 object-fit: cover 裁进卡片：xy 缩放，zw 偏移
uniform vec4 uCrop;
uniform vec2 uSize;
uniform float uMargin;
uniform float uOpacity;
uniform float uHover;
uniform float uLoaded;
uniform vec3 uAccent;
varying vec2 vUv;
float sdRound(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
void main() {
  vec2 p = (vUv - 0.5) * (uSize + 2.0 * uMargin);
  vec2 uv = p / uSize + 0.5;
  float d = sdRound(p, uSize * 0.5, min(uSize.x, uSize.y) * 0.04);
  float aa = max(fwidth(d), 1e-4);
  float inside = 1.0 - smoothstep(-aa, aa, d);
  // 缩略图到之前先用照片的主色垫底
  vec3 photo = uLoaded > 0.5 ? texture2D(uMap, clamp(uv, 0.0, 1.0) * uCrop.xy + uCrop.zw).rgb : uAccent * 0.35;
  photo *= 1.0 - 0.18 * smoothstep(0.3, 0.78, length(uv - 0.5));
  vec3 rim = mix(uAccent, vec3(1.0), 0.35);
  float ring = (1.0 - smoothstep(0.0, aa * 1.5 + 0.07, abs(d + 0.035))) * (0.3 + 0.7 * uHover);
  // 外晕在留白内平滑归零：sRGB 会放大接近黑色的值，被截断就会出现硬边框（同原型）
  float halo = (1.0 - inside) * exp(-max(d, 0.0) * 1.7) * (1.0 - smoothstep(0.0, uMargin * 0.9, d)) * (0.12 + 0.4 * uHover);
  vec3 col = mix(photo, rim * 1.2, ring * 0.5 * inside);
  col = mix(rim, col, inside);
  float alpha = max(inside, max(ring, halo));
  // 背面像照片背面：深色，只留一圈淡淡的边
  if (!gl_FrontFacing) {
    col = vec3(0.007, 0.007, 0.018) + rim * ring * 0.25;
    alpha *= 0.85;
  }
  alpha *= uOpacity;
  if (alpha < 0.001) discard;
  // 预乘透明度，post.ts 按预乘叠到画面上
  gl_FragColor = vec4(min(col, vec3(1.0)) * alpha, alpha);
}
`;

interface Slot {
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  /** 现在显示哪颗星的照片；-1 是还没分配 */
  star: number;
  /** 这一帧所在的虚拟格号（一天的照片比格子多时，同一格转一圈后换成后面的照片） */
  v: number;
  hover: number;
}

interface Pic {
  tex: THREE.Texture | null;
  /** 由 load 创建的 blob 网址，纹理释放时一起收回 */
  url: string | null;
  /** 0 还没有图，1 占位图，2 清晰图（或者没有更清晰的了） */
  level: 0 | 1 | 2;
  ctrl: AbortController | null;
  used: number;
  w: number;
  h: number;
}

const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const mod = (a: number, n: number) => ((a % n) + n) % n;

export class Carousel {
  private readonly group = new THREE.Group();
  private readonly plane = new THREE.PlaneGeometry(1, 1);
  private readonly blank: THREE.Texture;
  private readonly maxSlots: number;
  /** 光环之外多留几张纹理，转回来时不用重新加载 */
  private readonly spare: number;
  private readonly reduced: boolean;
  private slots: Slot[] = [];
  /** 这一天的星，按添加顺序；aspects 是对应照片的宽高比 */
  private stars: number[] = [];
  private aspects: number[] = [];
  private readonly pics = new Map<number, Pic>();
  private hooks: CarouselHooks | null = null;
  /** 0 在星里，1 在光环上；goal 是要去的那头 */
  private blend = 0;
  private goal = 0;
  private angle = 0;
  private angleGoal = 0;
  private grabbed = false;
  private grabX = 0;
  private idle = 0;
  private now = 0;
  private wave = 1;
  private frontStar = -1;
  private hovered = -1;
  private loading = 0;
  private readonly raycaster = new THREE.Raycaster();
  private readonly from = new THREE.Vector3();
  private readonly slotPos = new THREE.Vector3();
  private readonly slotQ = new THREE.Quaternion();
  private readonly camQ = new THREE.Quaternion();
  private readonly accent = new THREE.Color();

  /** parent：叠加层的场景（post.ts 在色调映射之后叠上去） */
  constructor(parent: THREE.Object3D, opts: { maxSlots: number; spare: number; reduced: boolean }) {
    this.maxSlots = opts.maxSlots;
    this.spare = opts.spare;
    this.reduced = opts.reduced;
    this.group.scale.setScalar(K);
    parent.add(this.group);
    const blank = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    blank.needsUpdate = true;
    this.blank = blank;
  }

  /** 光环在画面上（进出的动画也算） */
  get visible(): boolean {
    return this.slots.length > 0;
  }

  /** 光环在、能转能点（收起的动画开始后就不算了） */
  get active(): boolean {
    return this.goal === 1 && this.slots.length > 0;
  }

  /** 0 在星里，1 在光环上 */
  get progress(): number {
    return this.slots.length ? this.blend : 0;
  }

  /** 正面那张的星 */
  get front(): number {
    return this.frontStar;
  }

  get callbacks(): CarouselHooks | null {
    return this.hooks;
  }

  private get slotAngle(): number {
    return (Math.PI * 2) / Math.max(1, this.slots.length);
  }

  private radius(): number {
    return Math.max(46, (this.slots.length * 14.5) / (Math.PI * 2));
  }

  /** 展开光环：center 是星团中心，facing 是镜头的位置（正面朝着它） */
  enter(o: { stars: number[]; start: number; aspects: number[]; center: THREE.Vector3; facing: THREE.Vector3; hooks: CarouselHooks }): void {
    this.dispose();
    this.stars = o.stars.slice();
    this.aspects = o.aspects.slice();
    this.hooks = o.hooks;
    this.group.position.copy(o.center).addScaledVector(UP, LIFT * K);
    this.group.quaternion.setFromAxisAngle(UP, Math.atan2(o.facing.x - o.center.x, o.facing.z - o.center.z));
    this.group.updateMatrixWorld(true);
    // 只有两张时 12 格会让同一张出现在正面两侧，改用前后两格
    const P = this.stars.length <= 2 ? this.stars.length : this.maxSlots;
    for (let p = 0; p < P; p++) {
      const material = new THREE.ShaderMaterial({
        uniforms: {
          uMap: { value: this.blank },
          uCrop: { value: new THREE.Vector4(1, 1, 0, 0) },
          uSize: { value: new THREE.Vector2(10, 10) },
          uMargin: { value: MARGIN },
          uOpacity: { value: 0 },
          uHover: { value: 0 },
          uLoaded: { value: 0 },
          uAccent: { value: new THREE.Color() },
        },
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        premultipliedAlpha: true,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(this.plane, material);
      mesh.frustumCulled = false;
      this.group.add(mesh);
      this.slots.push({ mesh, star: -1, v: Number.NaN, hover: 0 });
    }
    this.angle = this.angleGoal = o.start * this.slotAngle;
    this.blend = 0;
    this.goal = 1;
    this.wave = this.reduced ? 0 : 1;
    this.idle = this.now;
  }

  /** 收起：照片飞回各自的星，飞完就把卡片和纹理都释放掉 */
  leave(): void {
    this.goal = 0;
    this.grabbed = false;
    this.hovered = -1;
  }

  /** 看光环正面那张的镜头位置 */
  cameraPose(aspect: number, fov: number): { pos: THREE.Vector3; target: THREE.Vector3 } {
    const R = this.radius();
    const front = this.group.localToWorld(new THREE.Vector3(0, 0, R).applyQuaternion(TILT));
    const nrm = new THREE.Vector3(0, 0, 1).applyQuaternion(TILT).applyQuaternion(this.group.quaternion);
    // 原型的镜头视角是 55°，这里是 50°，按视野换算距离；竖屏离远一些（同原型）
    const fit = Math.tan(THREE.MathUtils.degToRad(55) / 2) / Math.tan(THREE.MathUtils.degToRad(fov) / 2);
    const dist = ((aspect >= 1 ? 34 : 34 / Math.pow(aspect, 0.85)) + R * 0.12) * K * fit;
    return {
      pos: front.clone().addScaledVector(nrm, dist).addScaledVector(UP, 14 * K),
      target: front.clone().addScaledVector(nrm, -R * 0.5 * K).addScaledVector(UP, -AIM_DOWN * K),
    };
  }

  update(dt: number, camera: THREE.Camera, starAt: (i: number, out: THREE.Vector3) => THREE.Vector3 | null, colorAt: (i: number, out: THREE.Color) => THREE.Color): void {
    this.now += dt;
    if (!this.slots.length) return;
    // 原型：进出都是 2 秒的线性补间，每张卡按离正面的角距离错峰
    const speed = 1 / (this.reduced ? 1.2 : 2.0);
    this.blend = this.goal > this.blend ? Math.min(this.goal, this.blend + dt * speed) : Math.max(this.goal, this.blend - dt * speed);
    if (this.goal === 0 && this.blend === 0) {
      this.dispose();
      return;
    }
    const P = this.slots.length, n = this.stars.length, st = this.slotAngle, R = this.radius(), conveyor = n !== P;
    // 松手 0.25 秒后吸附到最近一格
    if (this.goal === 1 && !this.grabbed && this.now - this.idle > 0.25) this.angleGoal = Math.round(this.angleGoal / st) * st;
    this.angle += (this.angleGoal - this.angle) * (1 - Math.exp(-6 * dt));
    this.wave += ((this.reduced ? 0 : 1) - this.wave) * (1 - Math.exp(-3 * dt));
    const F = this.angle / st, fk = mod(Math.round(F), n);
    if (this.stars[fk] !== this.frontStar) {
      this.frontStar = this.stars[fk]!;
      this.hooks?.onFront(this.frontStar, fk, n);
    }
    // 镜头的朝向换到光环的局部坐标：卡片起飞前面向镜头
    this.camQ.copy(this.group.quaternion).invert().multiply(camera.quaternion);
    const want: { star: number; w: number }[] = [];
    for (let p = 0; p < P; p++) {
      const s = this.slots[p]!, v = p + P * Math.round((F - p) / P), k = mod(v, n), star = this.stars[k]!;
      s.v = v;
      if (s.star !== star) this.assign(s, star, this.aspects[k]!, colorAt);
      const th = v * st - this.angle, w = Math.atan2(Math.sin(th), Math.cos(th)), front = (Math.cos(w) + 1) / 2;
      const be = easeInOutCubic(clamp01(this.blend * (1 + STAGGER) - (STAGGER * Math.abs(w)) / Math.PI));
      this.slotPos.set(Math.sin(th) * R, Math.sin(th * 2 + this.now * 0.6) * 1.2 * this.wave, Math.cos(th) * R).applyQuaternion(TILT);
      this.slotQ.setFromAxisAngle(UP, th).premultiply(TILT);
      // 起点是这张照片的星
      if (starAt(star, this.from)) this.group.worldToLocal(this.from);
      else this.from.copy(this.slotPos);
      const m = s.mesh, u = m.material.uniforms;
      m.position.lerpVectors(this.from, this.slotPos, be);
      if (be > 0 && be < 1 && !this.reduced) m.position.y += Math.sin(be * Math.PI) * 14;
      m.quaternion.slerpQuaternions(this.camQ, this.slotQ, be);
      s.hover += ((this.hovered === p ? 1 : 0) - s.hover) * (1 - Math.exp(-9 * dt));
      const size = u.uSize!.value as THREE.Vector2;
      const scale = THREE.MathUtils.lerp(0.04, 0.82 + (FRONT_SCALE - 0.82) * front, be) * (1 + s.hover * 0.12);
      m.scale.set((size.x + 2 * MARGIN) * scale, (size.y + 2 * MARGIN) * scale, 1);
      u.uHover!.value = s.hover;
      // 背面暗一些；照片和格子数不一样时，背面那一格会换照片，换之前先淡掉
      u.uOpacity!.value = smoothstep(0, 0.25, be) * THREE.MathUtils.lerp(1, 0.3 + 0.7 * front, be) * (conveyor ? 1 - smoothstep(0.78 * Math.PI, 0.97 * Math.PI, Math.abs(w)) : 1);
      this.bind(s, star);
      want.push({ star, w: Math.abs(w) });
    }
    this.fetch(want);
    this.evict(want);
  }

  private assign(s: Slot, star: number, aspect: number, colorAt: (i: number, out: THREE.Color) => THREE.Color): void {
    s.star = star;
    // 同原型：卡片面积 100，宽高比按照片（和面板一样限制在 0.56–1.8）
    const a = Math.min(1.8, Math.max(0.56, aspect || 4 / 3)), H = Math.sqrt(100 / a);
    const u = s.mesh.material.uniforms;
    (u.uSize!.value as THREE.Vector2).set(a * H, H);
    (u.uAccent!.value as THREE.Color).copy(colorAt(star, this.accent));
  }

  private pic(star: number): Pic {
    let p = this.pics.get(star);
    if (!p) this.pics.set(star, (p = { tex: null, url: null, level: 0, ctrl: null, used: this.now, w: 1, h: 1 }));
    return p;
  }

  private bind(s: Slot, star: number): void {
    const pic = this.pic(star), u = s.mesh.material.uniforms;
    pic.used = this.now;
    u.uLoaded!.value = pic.tex ? 1 : 0;
    u.uMap!.value = pic.tex ?? this.blank;
    if (!pic.tex) return;
    const size = u.uSize!.value as THREE.Vector2, ca = size.x / size.y, ia = pic.w / Math.max(1, pic.h);
    const sx = Math.min(1, ca / ia), sy = Math.min(1, ia / ca);
    (u.uCrop!.value as THREE.Vector4).set(sx, sy, (1 - sx) / 2, (1 - sy) / 2);
  }

  /** 先占位图、再清晰图；离正面近的先加载，同时最多 4 张 */
  private fetch(want: { star: number; w: number }[]): void {
    const hooks = this.hooks;
    if (!hooks || this.goal === 0) return;
    for (const { star } of [...want].sort((a, b) => a.w - b.w)) {
      if (this.loading >= 4) break;
      const pic = this.pic(star);
      if (pic.level === 2 || pic.ctrl) continue;
      const ctrl = (pic.ctrl = new AbortController());
      this.loading++;
      void (async () => {
        try {
          if (pic.level === 0) {
            const ph = await hooks.placeholder(star);
            if (ctrl.signal.aborted) return;
            if (ph && pic.level === 0) this.setPic(pic, ph, 1);
          }
          const img = await hooks.load(star, ctrl.signal);
          if (ctrl.signal.aborted) {
            if (img instanceof HTMLImageElement && img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
            return;
          }
          if (img) this.setPic(pic, img, 2);
          else pic.level = 2;
        } catch {
          // 读不出来就停在占位图，不再重试
          if (!ctrl.signal.aborted) pic.level = 2;
        } finally {
          this.loading--;
          if (pic.ctrl === ctrl) pic.ctrl = null;
        }
      })();
    }
  }

  private setPic(pic: Pic, img: CardImage, level: 1 | 2): void {
    this.dropTex(pic);
    let tex: THREE.Texture;
    if (img instanceof HTMLCanvasElement) {
      // canvas 按 ImageData 上传，绕开 Safari 上传 canvas 元素时的坏点（见 galaxy.ts 的 canvasTexture）
      tex = new THREE.Texture(img.getContext('2d')!.getImageData(0, 0, img.width, img.height));
      pic.w = img.width;
      pic.h = img.height;
    } else {
      tex = new THREE.Texture(img);
      // 照片不透明，预乘与否结果一样；预乘上传也能绕开 Safari 的那个坏点
      tex.premultiplyAlpha = true;
      pic.w = img.naturalWidth;
      pic.h = img.naturalHeight;
      if (img.src.startsWith('blob:')) pic.url = img.src;
    }
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    // 侧面的卡片斜着看，各向异性过滤让照片不糊
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    pic.tex = tex;
    pic.level = level;
  }

  private dropTex(pic: Pic): void {
    pic.tex?.dispose();
    pic.tex = null;
    if (pic.url) URL.revokeObjectURL(pic.url);
    pic.url = null;
  }

  /** 槽位固定：不在光环上的照片超出容量就按最近最少使用回收 */
  private evict(want: { star: number }[]): void {
    const cap = this.slots.length + this.spare;
    if (this.pics.size <= cap) return;
    const keep = new Set(want.map((x) => x.star));
    const old = [...this.pics.entries()].filter(([s]) => !keep.has(s)).sort((a, b) => a[1].used - b[1].used);
    for (const [s, pic] of old) {
      if (this.pics.size <= cap) break;
      pic.ctrl?.abort();
      this.dropTex(pic);
      this.pics.delete(s);
    }
  }

  private dispose(): void {
    for (const s of this.slots) {
      this.group.remove(s.mesh);
      s.mesh.material.dispose();
    }
    this.slots = [];
    for (const pic of this.pics.values()) {
      pic.ctrl?.abort();
      this.dropTex(pic);
    }
    this.pics.clear();
    this.hooks = null;
    this.stars = [];
    this.aspects = [];
    this.frontStar = -1;
    this.hovered = -1;
    this.grabbed = false;
  }

  // ── 操作 ──

  grab(x: number): void {
    this.grabbed = true;
    this.grabX = x;
    this.idle = this.now;
  }

  drag(x: number): void {
    if (!this.grabbed) return;
    const dx = x - this.grabX;
    this.grabX = x;
    this.angleGoal -= dx * 0.0065 * (60 / Math.max(this.radius(), 40));
    this.idle = this.now;
  }

  release(): void {
    this.grabbed = false;
    this.idle = this.now;
  }

  wheel(delta: number): void {
    this.angleGoal += delta * 0.0024;
    this.idle = this.now;
  }

  step(d: number): void {
    const st = this.slotAngle;
    this.angleGoal = (Math.round(this.angleGoal / st) + d) * st;
    this.idle = this.now;
  }

  /** 指针下的卡片；front 表示是正面那张 */
  pick(ndc: THREE.Vector2, camera: THREE.Camera): { slot: number; front: boolean } | null {
    if (!this.active) return null;
    this.raycaster.setFromCamera(ndc, camera);
    const meshes = this.slots.filter((s) => (s.mesh.material.uniforms.uOpacity!.value as number) > 0.15).map((s) => s.mesh);
    const hit = this.raycaster.intersectObjects(meshes, false)[0];
    if (!hit) return null;
    const slot = this.slots.findIndex((s) => s.mesh === hit.object);
    return { slot, front: this.slots[slot]!.v === Math.round(this.angle / this.slotAngle) };
  }

  /** 悬停：卡片放大一点、亮起边框；返回指针下有没有卡片 */
  hover(ndc: THREE.Vector2 | null, camera: THREE.Camera): boolean {
    const hit = ndc && !this.grabbed ? this.pick(ndc, camera) : null;
    this.hovered = hit ? hit.slot : -1;
    return hit !== null;
  }

  /** 把这一格转到正面 */
  spinTo(slot: number): void {
    const s = this.slots[slot];
    if (!s) return;
    this.angleGoal = s.v * this.slotAngle;
    this.idle = this.now;
  }
}
